import React, { Suspense, lazy, useCallback, useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { CircleUserRound, ClipboardList } from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { hasPermission } from "../../utils/permissionUtils";
import {
  MY_ASSIGNED_TICKETS,
  CHANGE_REQUESTS_ASSIGNED_TO_ME,
} from "../../constants/permissionKeys";
import Loader from "../../Loader";

const AllTickets = lazy(() => import("../TicketManagement/AllTickets"));
const ChangeRequestMyAssigned = lazy(
  () => import("../ChangeRequests/ChangeRequestMyAssigned"),
);

const TAB_TICKETS = "tickets";
const TAB_CHANGE_REQUESTS = "change-requests";

/**
 * Unified assigned-to-me shell: tickets (AllTickets) and change requests
 * (GET /change-requests/my-assigned) via tabs.
 */
const MyAssignedHub = ({ initialTab }) => {
  const entitlement = useAppSelector((state) => state.userEntitlement?.data);
  const [searchParams, setSearchParams] = useSearchParams();

  const canTickets = hasPermission(entitlement, MY_ASSIGNED_TICKETS);
  const canChangeRequests = hasPermission(entitlement, CHANGE_REQUESTS_ASSIGNED_TO_ME);

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (canTickets) {
      tabs.push({
        id: TAB_TICKETS,
        label: "Tickets",
        icon: CircleUserRound,
      });
    }
    if (canChangeRequests) {
      tabs.push({
        id: TAB_CHANGE_REQUESTS,
        label: "Change Requests",
        icon: ClipboardList,
      });
    }
    return tabs;
  }, [canTickets, canChangeRequests]);

  const tabFromUrl = searchParams.get("tab");
  const resolvedTab = useMemo(() => {
    if (tabFromUrl && availableTabs.some((t) => t.id === tabFromUrl)) {
      return tabFromUrl;
    }
    if (initialTab && availableTabs.some((t) => t.id === initialTab)) {
      return initialTab;
    }
    return availableTabs[0]?.id ?? null;
  }, [tabFromUrl, initialTab, availableTabs]);

  const setActiveTab = useCallback(
    (tabId) => {
      const next = new URLSearchParams(searchParams);
      if (tabId === availableTabs[0]?.id) {
        next.delete("tab");
      } else {
        next.set("tab", tabId);
      }
      setSearchParams(next, { replace: true });
    },
    [availableTabs, searchParams, setSearchParams],
  );

  if (!canTickets && !canChangeRequests) {
    return <Navigate to="/unauthorized" replace />;
  }

  const showTabs = availableTabs.length > 1;

  return (
    <div className="max-w-full mx-auto space-y-3">
      {showTabs ? (
        <div className="inline-flex gap-0.5 rounded-md border border-secondary-200 bg-secondary-50 p-0.5">
          {availableTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = resolvedTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-white text-primary-700 shadow-sm"
                    : "text-secondary-600 hover:bg-white/80 hover:text-secondary-900"
                }`}
              >
                <Icon size={13} strokeWidth={1.75} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      <Suspense fallback={<Loader />}>
        {resolvedTab === TAB_TICKETS ? <AllTickets /> : null}
        {resolvedTab === TAB_CHANGE_REQUESTS ? <ChangeRequestMyAssigned /> : null}
      </Suspense>
    </div>
  );
};

export default MyAssignedHub;
